import { ToastNotification } from "@carbon/react";
import { useTranslation } from "react-i18next";
import { NotificationType } from "../../../context/types";
import "./notification.scss";

type NotificationProps = {
  handleShow: () => void;
  message?: string;
  /** Explicit severity. When omitted, falls back to inferring error-vs-success from `ERROR_KEYS`. */
  type?: NotificationType;
  show: boolean;
};

/** i18n keys of the toasts that must render as errors (the rest render as success). */
const ERROR_KEYS = [
  "newMaterialForm.error",
  "newMaterialForm.materialFamilyExists",
  "purchaseOrders.create.error",
  "purchaseOrders.edit.error",
  "purchaseOrders.edit.notDraft",
  "purchaseOrders.actions.sendError",
  "purchaseOrders.actions.resendError",
  "purchaseOrders.actions.remindError",
  "purchaseOrders.approve.error",
  "purchaseOrders.approve.notApprover",
  "purchaseOrders.approve.notPending",
  "purchaseOrders.reject.error",
  "purchaseOrders.reject.notApprover",
  "purchaseOrders.reject.notPending",
  "purchaseOrders.coordinate.error",
  "purchaseOrders.coordinate.notAccepted",
  "purchaseOrders.supplier.error",
  "purchaseOrders.supplier.notFound",
  "purchaseOrders.supplier.alreadyResponded",
  "purchaseOrders.approvalRules.invalid",
  "purchaseOrders.approvalRules.error",
];

/**
 * The app's global toast (driven by `NotificationProvider`/`openNotification`) — rebuilt on Carbon's
 * `ToastNotification`, replacing the pre-Carbon Tailwind `bg-green-700`/`bg-red-700` toast. Carbon
 * supplies the kind-based colour, icon and close button; a small fixed wrapper only positions it
 * top-right under the header. An explicit `type` is honored as the Carbon kind; untyped callers
 * (legacy `openNotification(message)`) fall back to the old split — the known error messages render
 * as `error`, everything else as `success`.
 */
const Notification = ({
  handleShow,
  message,
  type,
  show,
}: NotificationProps) => {
  const { t } = useTranslation();

  if (!show || !message) return null;

  const kind: NotificationType =
    type ??
    (ERROR_KEYS.some((key) => message === t(key)) ? "error" : "success");

  return (
    <div className="app-toast" aria-live="assertive">
      <ToastNotification
        kind={kind}
        lowContrast
        title={message}
        onClose={() => {
          handleShow();
          return false;
        }}
        className="app-toast__notification"
      />
    </div>
  );
};

export default Notification;
